'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type LifecyclePrompt = {
  key: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  variant: 'upgrade' | 'trial';
};

/**
 * Renders rate-limited, lifecycle-derived upgrade nudges. The server decides
 * eligibility and cooldown (`/api/lifecycle`); this component only presents and
 * records dismissals. Lifecycle never grants capabilities.
 */
export function UpgradePrompts({ className }: { className?: string }) {
  const [prompts, setPrompts] = useState<LifecyclePrompt[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/lifecycle', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.prompts)) {
          setPrompts(data.prompts);
        }
      } catch {
        // Nudges are best-effort; never block the dashboard.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss(key: string) {
    setPrompts((current) => current.filter((p) => p.key !== key));
    void fetch(`/api/lifecycle/prompts/${key}/dismiss`, {
      method: 'POST',
    }).catch(() => undefined);
  }

  if (prompts.length === 0) return null;

  return (
    <div className={cn('space-y-3', className)}>
      {prompts.map((prompt) => (
        <div
          key={prompt.key}
          className={cn(
            'relative flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
            prompt.variant === 'trial'
              ? 'border-indigo-200 bg-indigo-50'
              : 'border-blue-200 bg-blue-50'
          )}
        >
          <div className="flex items-start gap-3 pr-6">
            <div
              className={cn(
                'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                prompt.variant === 'trial'
                  ? 'bg-indigo-100 text-indigo-600'
                  : 'bg-blue-100 text-blue-600'
              )}
            >
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">
                {prompt.title}
              </p>
              <p className="mt-0.5 text-sm text-slate-600">{prompt.body}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:shrink-0">
            <Button asChild size="sm" className="rounded-lg">
              <Link href={prompt.ctaHref}>
                {prompt.ctaLabel}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>

          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => dismiss(prompt.key)}
            className="absolute right-2 top-2 rounded-md p-1 text-slate-400 transition-colors hover:bg-white/60 hover:text-slate-600 sm:static"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
